import { useEffect, useRef } from 'react';
import { mfTrackSection, mfTrackSectionClosed } from '@app/utils/mouseflowTracker';

/**
 * Emits a Mouseflow page action each time a child accordion (a table-of-contents
 * sub-menu item) opens, and a close tag when it collapses. The parent section's
 * own open/close is tracked in DealRoomLanding; this covers the sub-menu level,
 * which otherwise never produces a page action of its own.
 *
 * `labelFor` must resolve a key exactly the way that component's panel header
 * renders it — custom label first, otherwise the translated label — so the
 * Mouseflow page name matches the text the customer actually sees on screen.
 *
 * It fires on any change to `activeKey`, so a deep-link that opens a sub-menu by
 * URL hash is tracked just like a click.
 */
export const useSubMenuMouseflow = (activeKey: string, labelFor: (key: string) => string): void => {
  const prevRef = useRef<string>('');

  useEffect(() => {
    const prev = prevRef.current;
    if (activeKey === prev) return;

    if (activeKey) mfTrackSection(labelFor(activeKey));
    else if (prev) mfTrackSectionClosed(labelFor(prev));

    prevRef.current = activeKey;
    // Keyed on activeKey alone: the guard above dedupes, and re-tracking is not
    // wanted when only `labelFor`'s closure (t / subMenu) changes identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKey]);
};
